import { useEffect, useRef, useState } from 'react';
import { Button, Dialog } from '@etnas/design-system';
import { authApi } from '@/api';
import { announceSession, audienceOf, listenSession } from '@/api/sessionSync';
import { Modal } from '@/components/Modal';
import { useAuth } from './auth';

const WARN_BEFORE_S = 60;

/**
 * Idle timeout (guideline 70: warn before expiry, extend in one action, restore after re-auth).
 * On expiry the session locks; the workspace stays mounted beneath the lock screen so the same patient,
 * section and autosaved draft return after the same person signs in again.
 */
export function SessionTimeout() {
  const { me, lock, lockNow } = useAuth();
  const idleS = me?.session.idleTimeoutSeconds ?? 900;
  const last = useRef(Date.now());
  const broadcastAt = useRef(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const user = me?.user;

  useEffect(() => {
    if (lock || !user) return;
    const bump = () => {
      // Activity while the warning is open does not silently extend: the person must choose.
      if (remaining === null) {
        last.current = Date.now();
        // Heartbeats follow actual input, at most once per second. Receiving a heartbeat never
        // rebroadcasts it, so inactive tabs cannot keep each other alive.
        if (last.current - broadcastAt.current >= 1000) {
          broadcastAt.current = last.current;
          announceSession('activity', user);
        }
      }
    };
    const unlisten = listenSession(event => {
      if (event.action !== 'activity' || event.userId !== user.id || event.audience !== audienceOf(user) || !Number.isFinite(event.at)) return;
      const at = Math.min(Date.now(), event.at);
      if (at <= last.current) return;
      last.current = at;
      // Actual work in another tab also dismisses this tab's warning.
      setRemaining(null);
    });
    const evs = ['mousedown', 'keydown', 'wheel', 'touchstart'] as const;
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => {
      const left = Math.round(idleS - (Date.now() - last.current) / 1000);
      if (left <= 0) {
        setRemaining(null);
        lockNow('idle');
      } else if (left <= WARN_BEFORE_S) setRemaining(left);
      else if (remaining !== null) setRemaining(null);
    }, 1000);
    return () => {
      evs.forEach((e) => window.removeEventListener(e, bump));
      unlisten();
      clearInterval(t);
    };
  }, [idleS, lock, lockNow, remaining, user]);

  useEffect(() => {
    setRemaining(null);
    last.current = Date.now();
  }, [lock]);

  if (remaining === null || lock) return null;
  const stay = async () => {
    last.current = Date.now();
    setRemaining(null);
    announceSession('activity', user);
    await authApi.refresh();
  };
  return (
    <Modal onClose={stay} top>
      <Dialog
        title="Your session is about to lock"
        eyebrow="Inactivity"
        width={440}
        onClose={stay}
        footer={
          <>
            <Button variant="quiet" onClick={() => lockNow('idle')}>
              Lock now
            </Button>
            <Button variant="primary" icon="refresh" onClick={stay} data-autofocus>
              Stay signed in
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }} role="timer" aria-live="polite">
          For privacy, ETNAS locks after {Math.round(idleS / 60)} minutes without activity. Locking in{' '}
          <b className="ex-countdown is-low">{remaining} s</b>.
        </p>
        <p className="ex-small ex-fg-muted" style={{ margin: 0 }}>
          Drafts are saved. After you sign in again you return to the same patient and place.
        </p>
      </Dialog>
    </Modal>
  );
}
